import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { expertsApi, type ExpertsHistoryBill } from '../services/api';
import { Spinner } from '../components/shared/Spinner';

/**
 * Historique des bills générées via l'auto-facturation Experts.
 * Une ligne = un draft généré (1 ou 2 par consultant selon external /
 * internal_kestio).
 */

const KIND_LABEL: Record<ExpertsHistoryBill['kind'], string> = {
  external: 'Externe',
  internal_kestio: 'KESTIO',
};
const KIND_BADGE: Record<ExpertsHistoryBill['kind'], string> = {
  external: 'bg-blue-100 text-blue-800 ring-blue-200',
  internal_kestio: 'bg-purple-100 text-purple-800 ring-purple-200',
};

const STATUS_LABEL: Record<ExpertsHistoryBill['status'], string> = {
  success: '✓ OK',
  partial: '⚠️ Partiel',
  failed: '✕ Échec',
};
const STATUS_BADGE: Record<ExpertsHistoryBill['status'], string> = {
  success: 'bg-emerald-100 text-emerald-800 ring-emerald-200',
  partial: 'bg-amber-100 text-amber-800 ring-amber-200',
  failed: 'bg-red-100 text-red-800 ring-red-200',
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function ExpertsHistoryPage() {
  const [month, setMonth] = useState<string>(''); // vide = pas de filtre
  const [statusFilter, setStatusFilter] = useState<'' | 'success' | 'partial' | 'failed'>('');
  const [search, setSearch] = useState('');

  // On ne transmet le mois que s'il est COMPLET et valide (YYYY-MM) : un input
  // mois partiel (« 2025-0 ») renverrait un 400 et casserait la liste.
  const monthParam = /^\d{4}-(0[1-9]|1[0-2])$/.test(month) ? month : undefined;
  const q = useQuery({
    queryKey: ['experts', 'history', { month: monthParam, status: statusFilter }],
    queryFn: () =>
      expertsApi.history({
        month: monthParam,
        status: statusFilter || undefined,
        limit: 500,
      }),
    staleTime: 30_000,
  });

  const filtered = useMemo(() => {
    if (!q.data) return [];
    if (!search.trim()) return q.data.bills;
    const needle = search.trim().toLowerCase();
    return q.data.bills.filter((b) =>
      [b.consultant_name, b.bill_number]
        .filter(Boolean)
        .some((s) => s.toLowerCase().includes(needle)),
    );
  }, [q.data, search]);

  const totals = useMemo(() => {
    let amount = 0;
    let success = 0;
    let partial = 0;
    let failed = 0;
    for (const b of filtered) {
      amount += b.total_amount_ht ?? 0;
      if (b.status === 'success') success++;
      else if (b.status === 'partial') partial++;
      else failed++;
    }
    return { amount: Math.round(amount * 100) / 100, success, partial, failed };
  }, [filtered]);

  return (
    <div className="space-y-6">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Historique auto-facturation Experts
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Liste des bills générées via la page Auto-facturation. Une ligne = une bill
            (1 ou 2 par consultant selon kind).
          </p>
        </div>
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <label className="flex items-center gap-2">
          <span className="text-slate-600">Mois facturé</span>
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="rounded-md border border-slate-300 px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {month && (
            <button
              type="button"
              onClick={() => setMonth('')}
              className="text-xs text-slate-500 hover:text-slate-700"
              title="Voir tous les mois"
            >
              ✕
            </button>
          )}
        </label>
        <label className="flex items-center gap-2">
          <span className="text-slate-600">Statut</span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'success' | 'partial' | 'failed' | '')}
            className="rounded-md border border-slate-300 px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Tous</option>
            <option value="success">✓ OK</option>
            <option value="partial">⚠️ Partiel</option>
            <option value="failed">✕ Échec</option>
          </select>
        </label>
        <input
          type="search"
          placeholder="Rechercher (consultant, n° de bill)…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 min-w-[200px] max-w-xs rounded-md border border-slate-300 px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        {q.isFetching && <Spinner />}
      </div>

      {q.isLoading ? (
        <p className="text-slate-500">Chargement…</p>
      ) : q.isError ? (
        <p className="text-red-600">Erreur : {String(q.error)}</p>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 p-8 text-center text-slate-500">
          Aucune bill dans l'historique pour ces filtres.
        </div>
      ) : (
        <>
          {/* Résumé */}
          <div className="bg-white rounded-lg border border-slate-200 px-4 py-3 flex flex-wrap gap-x-6 gap-y-2 text-sm">
            <span>
              <strong>{filtered.length}</strong> bill(s)
            </span>
            <span className="text-emerald-700">{totals.success} OK</span>
            {totals.partial > 0 && <span className="text-amber-700">{totals.partial} partielles</span>}
            {totals.failed > 0 && <span className="text-red-700">{totals.failed} en échec</span>}
            <span className="font-medium ml-auto">
              Total HT : {totals.amount.toFixed(2)} €
            </span>
          </div>

          {/* Tableau */}
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-left text-xs uppercase tracking-wide">
                <tr>
                  <th className="px-3 py-2 font-medium">Mois</th>
                  <th className="px-3 py-2 font-medium">N° facture</th>
                  <th className="px-3 py-2 font-medium">Consultant</th>
                  <th className="px-3 py-2 font-medium">Kind</th>
                  <th className="px-3 py-2 font-medium text-right">HT</th>
                  <th className="px-3 py-2 font-medium text-right">TVA</th>
                  <th className="px-3 py-2 font-medium">Statut</th>
                  <th className="px-3 py-2 font-medium">Générée le</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filtered.map((b) => (
                  <tr key={b.id} className="hover:bg-slate-50">
                    <td className="px-3 py-2 tabular-nums whitespace-nowrap">{b.month}</td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {b.bill_number}
                      {b.bill_already_existed && (
                        <span className="ml-1 text-[10px] italic text-slate-400">(existant)</span>
                      )}
                    </td>
                    <td className="px-3 py-2 font-medium truncate max-w-[200px]" title={b.consultant_name}>
                      {b.consultant_name}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${KIND_BADGE[b.kind]}`}
                      >
                        {KIND_LABEL[b.kind]}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {b.total_amount_ht != null ? `${b.total_amount_ht.toFixed(2)} €` : '-'}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-xs text-slate-500">
                      {b.vat_rate != null ? `${b.vat_rate}%` : '-'}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${STATUS_BADGE[b.status]}`}
                        title={b.errors.length > 0 ? b.errors.join(' · ') : undefined}
                      >
                        {STATUS_LABEL[b.status]}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap">
                      {formatDateTime(b.created_at)}
                      {b.created_by && (
                        <div className="text-[10px] text-slate-400 truncate max-w-[160px]" title={b.created_by}>
                          {b.created_by}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
